"use client"; // ensures this file is treated as a client component (for Next.js App Router)

import React, {useEffect, useState, useSyncExternalStore} from 'react';
import {useRouter} from 'next/navigation'; // if you're using Next.js 13+ with app directory, otherwise use next/router for pages directory
import {isSignInWithEmailLink, signInWithEmailLink} from "firebase/auth";

import {Mail} from "lucide-react"

import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card"
import {APP_LOCAL_STORAGE_EMAIL_FOR_SIGNIN, APP_LOCAL_STORAGE_RETURN_URL, isValidReturnUrl} from "@/lib/constants";
import {getFirebaseAuth} from "@/lib/firebase/client-app";
import {useTranslations} from "next-intl";

interface CheckMailProps {
    pageTitle: string;
}

const CheckMail: React.FC<CheckMailProps> = ({pageTitle}) => {
    const t = useTranslations("auth.finishEmailSignup");
    const [message, setMessage] = useState(() => t("checkingEmail"));
    const email = useSyncExternalStore(
        () => () => {},
        () => window.localStorage.getItem(APP_LOCAL_STORAGE_EMAIL_FOR_SIGNIN),
        () => null,
    );
    const router = useRouter();

    useEffect(() => {
        const currentUrl = window.location.href;
        const storedEmail = window.localStorage.getItem(APP_LOCAL_STORAGE_EMAIL_FOR_SIGNIN);

        if (isSignInWithEmailLink(getFirebaseAuth(), currentUrl)) {

            // Try to get the email from localStorage (if available)
            let emailForSignIn = storedEmail;
            if (!emailForSignIn) {
                // If not available (e.g., different device), ask user to confirm
                emailForSignIn = window.prompt(t("emailConfirmPrompt"));
            }

            if (emailForSignIn) {
                signInWithEmailLink(getFirebaseAuth(), emailForSignIn, currentUrl)
                    .then(async () => {
                        // Remove the email from storage now that sign-in is complete
                        window.localStorage.removeItem(APP_LOCAL_STORAGE_EMAIL_FOR_SIGNIN);

                        setMessage(t("signedInSuccess"));

                        const storedReturnUrl = localStorage.getItem(APP_LOCAL_STORAGE_RETURN_URL);
                        localStorage.removeItem(APP_LOCAL_STORAGE_RETURN_URL);
                        router.push(isValidReturnUrl(storedReturnUrl) ? storedReturnUrl : "/home")
                    })
                    .catch((error) => {
                        console.error("Fehler bei der Anmeldung mit E-Mail-Link:", error);
                        setMessage(t("signInError", { message: error.message }));
                    });

            } else {
                setTimeout(() => setMessage(t("emailConfirmationRequired")), 0);
            }
        } else {
            setTimeout(() => setMessage(t("emailSentTo", { email: storedEmail ?? "" })), 0);
        }
    }, [router, t]);

    const CardIllustration = (
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary"/>
        </div>
    );

    if (email) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <div className="mx-auto flex w-full max-w-md flex-col items-center gap-4">
                    <h1 className="text-center text-2xl font-bold md:text-3xl">{pageTitle}</h1>
                    <Card className="w-full">
                        <CardHeader className="text-center">
                            {CardIllustration}
                            <CardTitle className="text-xl">{t("verifyingLinkTitle")}</CardTitle>
                            <CardDescription>{t("verifyingLinkSubtitle")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 text-center">
                            <p className="text-muted-foreground">{message}</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <div className="mx-auto flex w-full max-w-md flex-col items-center gap-4">
                <h1 className="text-center text-2xl font-bold md:text-3xl">{pageTitle}</h1>
                <Card className="w-full">
                    <CardHeader className="text-center">
                        {CardIllustration}
                        <CardTitle className="text-xl">{t("emailSentTitle")}</CardTitle>
                        <CardDescription>
                            {t("emailSentDescription")}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-center">
                        <p className="text-muted-foreground">
                            {t("emailSentBody")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            {t("checkSpam")}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );


};

export default CheckMail;
